import { IPhotoUploaderImage } from './MultiplePhotoUploader/multiplePhotoUploaderTypes';
import { Input_core_apimessages_ImageEntry } from '../../gql/graphql';
import ExifReader from 'exifreader';

export const MINIMUM_DIMENSION = 200; // (px) We will prevent images less than this on either side.
const MINIMUM_WARN_DIMENSION = 620; // (px) We will warn them if less than this on either side.

export function meetsMinimumWarnDimension(photo: IPhotoUploaderImage | Input_core_apimessages_ImageEntry) {
  return meetsMinimumDimensions(photo, MINIMUM_WARN_DIMENSION);
}

export function meetsMinimumDimension(photo: IPhotoUploaderImage | Input_core_apimessages_ImageEntry) {
  return meetsMinimumDimensions(photo, MINIMUM_DIMENSION);
}

function meetsMinimumDimensions(photo: IPhotoUploaderImage | Input_core_apimessages_ImageEntry, dimension: number): boolean {
  return photo.width >= dimension && photo.height >= dimension;
}

// https://developers.cloudflare.com/images/upload-images/#dimensions-and-sizes
export const MAX_DIMENSION_PIXELS = 10000;

export async function meetsMaximumDimensions(file: File): Promise<boolean> {
  const { height, width } = await parsePhotoDimensions(file);

  if (height > MAX_DIMENSION_PIXELS) {
    return false;
  }

  if (width > MAX_DIMENSION_PIXELS) {
    return false;
  }

  return true;
}

type PhotoDimensions = {
  width: number | null;
  height: number | null;
};

// See the Orientation tag section here for details: https://exiftool.org/TagNames/EXIF.html
function shouldSwapDimensions(orientationValue: number | null) {
  return [5, 6, 7, 8].includes(orientationValue);
}

export async function parsePhotoDimensions(file: File): Promise<PhotoDimensions> {
  // This can throw an error if an invalid image file is selected.
  // This will be caught by upstream components and render an error message to the user:
  // "There was a problem uploading your image. Please make sure your image is a .jpg, .png or .heic format and try again."
  const exifReaderTags = await ExifReader.load(file);

  // There are different exif tag names for height and width: https://exiv2.org/tags.html
  // It is unclear when any tag would be available or unavailable, so try many of them.
  const height =
    exifTagToNumber(exifReaderTags.ImageHeight) ||
    exifTagToNumber(exifReaderTags['Image Height']) ||
    exifTagToNumber(exifReaderTags.PixelYDimension);

  const width =
    exifTagToNumber(exifReaderTags.ImageWidth) ||
    exifTagToNumber(exifReaderTags['Image Width']) ||
    exifTagToNumber(exifReaderTags.PixelXDimension);

  const orientation = exifTagToNumber(exifReaderTags.Orientation);
  if (shouldSwapDimensions(orientation)) {
    return {
      height: width,
      width: height,
    };
  }

  return { height, width };
}

function exifTagToNumber(
  tag: ExifReader.XmpTag | ExifReader.NumberTag | { value: number } | null,
): number | null {
  if (!tag) {
    return null;
  }

  const value = tag.value;

  if (!value) {
    return null;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    return parseInt(value, 10);
  }

  return null;
}
